// src/constants.js  ── Design tokens, data constants, translations

// ── Design tokens ────────────────────────────────────────────────────────────
// CoastClear is an environmental-health surveillance product. Coordinators read
// it next to district health data and funders read it as evidence, so it is
// built to look like clinical software: quiet surfaces, one confident accent,
// and room to breathe.
//
// The surface is LIGHT. That is not a style preference — it is what makes the
// risk palette work. On the previous near-black ground, holding all three risk
// levels at 4.5:1 squeezed them into a narrow lightness band, and under
// simulated deuteranopia medium and high converged to dE2000 ≈ 10 (see
// NOTES.md). A light ground opens the whole lower half of the lightness range,
// so the trio can be spread far enough apart to survive both dichromacies.
//
// Every value below is measured, not chosen by eye. Each accent clears 4.5:1
// against ALL THREE surfaces (card, bg, surface), not just the lightest one,
// because these colours are read as small legend text as often as they are seen
// as marks.
export const C = {
  // Surfaces — cool off-white, near-neutral. The steps are small on purpose: a
  // card is separated from the page by its border and its shadow, not by a hard
  // tonal jump. `card` is the brightest because on a light ground the raised
  // thing is the white one.
  bg:      "#f5f8fa",   // page ground
  surface: "#eef3f7",   // inset rails, table stripes, the deepest surface
  card:    "#ffffff",   // panels and rows
  elevated:"#ffffff",   // modals and popovers — separated by shadow, not tone

  // The single accent. Navigation, primary action, "this is CoastClear".
  // A deep ocean teal-blue: confident at button size, still legible at 11px.
  primary: "#0d6a99",
  pLight:  "rgba(13,106,153,0.09)",
  pBorder: "rgba(13,106,153,0.26)",

  // Signals. One validated set, used for BOTH the UI meanings and the risk
  // levels, so nothing in the app carries a status colour that has not been
  // checked for colour-vision separation.
  //
  //   green  low risk    · all clear · resolved · done
  //   gold   medium risk · attention · reported · needs a human
  //   coral  high risk   · alert     · error    · destructive
  //
  // The trio is monotonic in lightness (L* 45.7 → 37.1 → 21.1) and the worst
  // pair under either simulated dichromacy is dE2000 = 18.4.
  green:   "#007b62",
  grLight: "rgba(0,123,98,0.10)",
  gold:    "#9b3600",
  gLight:  "rgba(155,54,0,0.10)",
  coral:   "#6b0027",
  cLight:  "rgba(107,0,39,0.09)",

  // People — headcounts, reach, demographics. A deep indigo, kept well away
  // from `primary` in lightness because under dichromacy two blues can only be
  // told apart by how dark they are.
  blue:    "#2e2673",
  blLight: "rgba(46,38,115,0.09)",

  // Text
  text:    "#10242e",
  muted:   "#4a6270",
  dim:     "#58707e",
  faint:   "rgba(16,36,46,0.045)",
  border:  "rgba(16,36,46,0.13)",

  // Shadows. On a light ground depth comes from shadow, not from a lighter
  // fill, so these are tokens rather than one-off values at the call site.
  shadowSm: "0 1px 2px rgba(16,36,46,0.06)",
  shadowMd: "0 2px 8px rgba(16,36,46,0.07), 0 1px 2px rgba(16,36,46,0.05)",
  shadowLg: "0 12px 32px rgba(16,36,46,0.14), 0 2px 8px rgba(16,36,46,0.07)",

  // Role colours
  reporter:"#0d6a99",
  cleanup: "#007b62",
  ngo:     "#9b3600",
};

// Semantic aliases — use these so intent is legible at the call site and a
// rethink is one line here.
//
//   people — anything counting human beings (reached, exposed, volunteers)
//   assist — machine suggestions awaiting a human decision
C.people = C.blue;
C.assist = C.gold;

// ── Partner brand colours ────────────────────────────────────────────────────
// Sampled from the partner logos themselves — k-means over the *eroded* core
// pixels of src/assets/partners/*.png, so JPEG edge fringing never reaches the
// average. These are NOT part of the status system and must never join it.
//
// Measured contrast against the three surfaces (card / bg / surface), and dE76
// separation from the risk trio under simulated deuteranopia and protanopia:
//
//   icenecdev    #138c1b   4.38 / 4.11 / 3.92  — fails 4.5, and under
//                          deuteranopia lands dE 8.5 from `gold` (medium
//                          risk). DECORATIVE ONLY.
//   ecotourGreen #245227   9.09 / 8.52 / 8.13  — passes as text, but sits
//                          dE 4.0 from `coral` (high risk) under
//                          deuteranopia. DECORATIVE ONLY.
//   ecotourGold  #f3cb25   1.57 / 1.47 / 1.40  — invisible on a light ground.
//                          DECORATIVE ONLY.
//   ecotourNavy  #0b242b  16.14 /15.13 /14.44  — an ink rather than a hue:
//                          dE 2.9 from `text`, so it carries no status
//                          meaning and is safe wherever text is safe.
//
// The rule those numbers force: the three chromatic values appear only as flat
// decoration in a partnership context — the accent rule on the partner band —
// and nowhere else. They never key a pill, a chart mark, a meaningful border,
// or any text. Every place a brand colour wanted to be a status colour, the
// status colour kept the job and the brand colour moved to the band.
export const BRAND = {
  icenecdev:    "#138c1b",
  ecotourNavy:  "#0b242b",
  ecotourGreen: "#245227",
  ecotourGold:  "#f3cb25",
};

// ── Card system ──────────────────────────────────────────────────────────────
// Three levels, and that is the whole vocabulary. A card earns a level from
// how much it is asking of the reader, never from how important it feels.
//
//   flat    — a row in a list. Reads as one of many.
//   raised  — a standalone panel. Reads as its own thing.
//   keyed   — carries a status colour, and ONLY when that status is the point
//             of the card. The colour sits in a full hairline border and a
//             wash, never a stripe down one edge.
//
// On a light ground the separation that a darker fill used to provide now comes
// from a hairline border plus a soft shadow, so every level carries one.
//
// Radii: 12 for rows, 16 for panels, 20 for a page-leading hero. Padding
// follows the same three steps.
export const card = (level = "flat", accent) => {
  const base = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: 12,
    boxShadow: C.shadowSm,
  };
  if (level === "raised")
    return { ...base, borderRadius: 16, padding: 16, boxShadow: C.shadowMd };
  if (level === "keyed")
    return {
      background: `linear-gradient(${accent}0f, ${accent}0f), ${C.card}`,
      border: `1px solid ${accent}3d`,
      borderRadius: 16,
      padding: 16,
      boxShadow: C.shadowSm,
    };
  return base;
};

// ── Type scale ───────────────────────────────────────────────────────────────
// Six steps. Used by the shared atoms in components/UI.jsx; screens are being
// migrated onto it a file at a time rather than in one sweeping edit.
export const TYPE = {
  hero:    { fontSize: 24, fontWeight: 800, letterSpacing: "-0.4px", lineHeight: 1.2 },
  title:   { fontSize: 19, fontWeight: 800, letterSpacing: "-0.2px", lineHeight: 1.25 },
  section: { fontSize: 15, fontWeight: 700, lineHeight: 1.3 },
  body:    { fontSize: 13.5, fontWeight: 500, lineHeight: 1.55 },
  meta:    { fontSize: 11.5, fontWeight: 500, lineHeight: 1.45 },
  micro:   { fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase" },
};

export const F = "'Plus Jakarta Sans', sans-serif";

// ── Breakpoints ──────────────────────────────────────────────────────────────
// Three sizes, matching how the app is actually used:
//
//   mobile  (< 768)  a volunteer on a phone in the field. Thumb-reachable
//                    bottom navigation, one column, nothing below 360px wide.
//   tablet  (768+)   a wider single column — still one flow, more room to
//                    breathe, and two-up where a pair of cards belongs together.
//   desktop (1024+)  a coordinator at a desk. Navigation moves to a side rail,
//                    and screens spread into the width instead of sitting in a
//                    phone-shaped column on an empty background.
//
// CONTENT is the readable cap for a single flow. Wider than this and lines of
// body text get too long to track, so screens that are mostly prose or a form
// stay at CONTENT while screens that are mostly data use WIDE.
export const BP = { tablet: 768, desktop: 1024 };
export const MAXW = { mobile: 480, tablet: 720, content: 760, wide: 1200 };


// ── Translations ─────────────────────────────────────────────────────────────
export const T = {
  en: {
    // Nav
    home:      "Home",
    report:    "Report",
    map:       "Map",
    learn:     "Learn",
    profile:   "Profile",
    // Auth
    tagline:   "Environmental-health surveillance",
    landTitle: "Catch the hazards that make people sick",
    landSub:   "Standing water, blocked drains and flooding drive malaria, cholera and typhoid along this coast. Log what you find; the record is what gets it dealt with.",
    signInSub: "Anything you captured offline is still on this phone.",
    registerSub: "Your name appears next to what you file, so a coordinator can follow up.",
    welcome:   "Welcome Aboard",
    langSub:   "Select your language to start protecting Cameroon's beaches.",
    selectRole:"Select Your Role",
    roleSub:   "This sets which screens you see. A coordinator can change it later.",
    signIn:    "Sign In",
    createAcc: "Create Account",
    email:     "Email",
    password:  "Password",
    name:      "Full Name",
    confirmPw: "Confirm Password",
    forgotPw:  "Forgot password?",
    noAccount: "Don't have an account?",
    hasAccount:"Already have an account?",
    // Dashboard
    dashboard:       "Dashboard",
    reportNow:       "Report Waste Now",
    yourStats:       "Your Stats",
    recentActivity:  "Recent Activity",
    latestReports:   "Latest Reports",
    // Report form
    reportHotspot:   "Report Hotspot",
    uploadPhoto:     "Upload Photo",
    photoSub:        "Take a picture of the waste hotspot",
    beachLocation:   "Beach Location",
    wasteType:       "Waste Type",
    severity:        "Severity Level",
    details:         "Additional Details",
    detailsPlaceholder: "Describe the waste, quantity, exact location...",
    submitReport:    "Submit Report",
    analyzeWithAI:   "Analyze with AI",
    // Map
    liveMap:    "Live Beach Map",
    // Blog
    learnTitle: "Learn & Explore",
    learnSub:   "Educational content about Cameroon's coastline",
    readMore:   "Read Article",
    // Profile
    myProfile:  "My Profile",
    badges:     "Badges",
    missions:   "Missions",
    store:      "Rewards Store",
    leaderboard:"Leaderboard",
    settings:   "Settings",
    signOut:    "Sign Out",
    // Notifications
    notifications: "Notifications",
    markAllRead:   "Mark all read",
    noNotifs:      "No notifications yet",
    // Common
    pts:       "pts",
    reports:   "Reports",
    cleanups:  "Cleanups",
    streak:    "Streak",
    days:      "days",
    claimReward:"Claim Reward",
    viewDetails:"View Details",
    confirm:   "Confirm",
    cancel:    "Cancel",
    back:      "Back",
    save:      "Save",
  },
  fr: {
    home:      "Accueil",
    report:    "Signaler",
    map:       "Carte",
    learn:     "Apprendre",
    profile:   "Profil",
    tagline:   "Surveillance de la santé environnementale",
    landTitle: "Repérer les dangers qui rendent malade",
    landSub:   "Eaux stagnantes, caniveaux bouchés et inondations alimentent le paludisme, le choléra et la typhoïde sur ce littoral. Signalez ce que vous voyez : c'est le relevé qui déclenche l'action.",
    signInSub: "Ce que vous avez saisi hors ligne est toujours sur ce téléphone.",
    registerSub: "Votre nom accompagne vos signalements, pour qu'un coordinateur puisse vous joindre.",
    welcome:   "Bienvenue",
    langSub:   "Choisissez votre langue pour protéger les plages du Cameroun.",
    selectRole:"Choisissez votre rôle",
    roleSub:   "Comment voulez-vous aider à garder les côtes du Cameroun propres?",
    signIn:    "Se connecter",
    createAcc: "Créer un compte",
    email:     "Email",
    password:  "Mot de passe",
    name:      "Nom complet",
    confirmPw: "Confirmer le mot de passe",
    forgotPw:  "Mot de passe oublié?",
    noAccount: "Pas encore de compte?",
    hasAccount:"Déjà un compte?",
    dashboard:       "Tableau de bord",
    reportNow:       "Signaler des déchets",
    yourStats:       "Vos statistiques",
    recentActivity:  "Activité récente",
    latestReports:   "Derniers signalements",
    reportHotspot:   "Signaler un point chaud",
    uploadPhoto:     "Télécharger une photo",
    photoSub:        "Prenez une photo du point de déchets",
    beachLocation:   "Lieu de la plage",
    wasteType:       "Type de déchet",
    severity:        "Niveau de gravité",
    details:         "Détails supplémentaires",
    detailsPlaceholder: "Décrivez les déchets, la quantité, l'emplacement exact...",
    submitReport:    "Soumettre le signalement",
    analyzeWithAI:   "Analyser avec l'IA",
    liveMap:    "Carte des plages en direct",
    learnTitle: "Apprendre et Explorer",
    learnSub:   "Contenu éducatif sur le littoral camerounais",
    readMore:   "Lire l'article",
    myProfile:  "Mon Profil",
    badges:     "Badges",
    missions:   "Missions",
    store:      "Boutique de récompenses",
    leaderboard:"Classement",
    settings:   "Paramètres",
    signOut:    "Se déconnecter",
    notifications: "Notifications",
    markAllRead:   "Tout marquer comme lu",
    noNotifs:      "Pas encore de notifications",
    pts:       "pts",
    reports:   "Signalements",
    cleanups:  "Nettoyages",
    streak:    "Série",
    days:      "jours",
    claimReward:"Réclamer la récompense",
    viewDetails:"Voir les détails",
    confirm:   "Confirmer",
    cancel:    "Annuler",
    back:      "Retour",
    save:      "Enregistrer",
  },
  pid: {
    home:      "Home",
    report:    "Report Am",
    map:       "Map",
    learn:     "Learn",
    profile:   "Profile",
    tagline:   "Watch for health for we coast",
    landTitle: "Catch the things wey di make people sick",
    landSub:   "Water wey stand, gutter wey block and flood na dem di bring malaria, cholera and typhoid for this coast. Report wetin you see — na the record di make them do something.",
    signInSub: "Wetin you enter when network no dey still dey this phone.",
    registerSub: "Your name di follow the report wey you send, so coordinator fit reach you.",
    welcome:   "Welcome Come",
    langSub:   "Choose the language wey you want use.",
    selectRole:"Choose Your Role",
    roleSub:   "How you wan help clean Cameroon coast?",
    signIn:    "Enter",
    createAcc: "Make Account",
    email:     "Email",
    password:  "Password",
    name:      "Your Name",
    confirmPw: "Confirm Password",
    forgotPw:  "You forget password?",
    noAccount: "You never get account?",
    hasAccount:"You get account already?",
    dashboard:       "Dashboard",
    reportNow:       "Report Rubbish Now",
    yourStats:       "Your Numbers",
    recentActivity:  "Recent Waka",
    latestReports:   "New Reports",
    reportHotspot:   "Report Rubbish",
    uploadPhoto:     "Upload Photo",
    photoSub:        "Take photo of the rubbish",
    beachLocation:   "Which Beach?",
    wasteType:       "Which Rubbish?",
    severity:        "How Bad E Be?",
    details:         "Extra Info",
    detailsPlaceholder: "Talk say more about the rubbish, where e dey, how plenty...",
    submitReport:    "Submit Report",
    analyzeWithAI:   "AI Go Look Am",
    liveMap:    "Live Beach Map",
    learnTitle: "Learn Plenty Thing",
    learnSub:   "Learn about Cameroon beach wahala",
    readMore:   "Read Am",
    myProfile:  "My Profile",
    badges:     "Badges",
    missions:   "Missions",
    store:      "Rewards Shop",
    leaderboard:"Who Win",
    settings:   "Settings",
    signOut:    "Comot",
    notifications: "Notifications",
    markAllRead:   "Mark all done read",
    noNotifs:      "No notification yet",
    pts:       "pts",
    reports:   "Reports",
    cleanups:  "Cleanups",
    streak:    "Streak",
    days:      "days",
    claimReward:"Collect Reward",
    viewDetails:"See Details",
    confirm:   "Confirm",
    cancel:    "Cancel",
    back:      "Back",
    save:      "Save",
  },
};

export const tx = (lang, key) => T[lang]?.[key] || T.en[key] || key;

// ── Ranks ─────────────────────────────────────────────────────────────────────
const RANKS = [
  { icon:"beach_access",  title:"Beach Scout",       min:0,    max:199,   color:"#94a3b8" },
  { icon:"water",         title:"Tide Watcher",      min:200,  max:499,   color:"#38bdf8" },
  { icon:"cruelty_free",  title:"Shore Keeper",      min:500,  max:999,   color:"#fb923c" },
  { icon:"waves",         title:"Wave Rider",        min:1000, max:1999,  color:C.primary },
  { icon:"scuba_diving",  title:"Deep Diver",        min:2000, max:3999,  color:"#a78bfa" },
  { icon:"shield",        title:"Ocean Guardian",    min:4000, max:7999,  color:C.gold    },
  { icon:"public",        title:"CoastClear Legend", min:8000, max:99999, color:"#f472b6" },
];

export const getRank  = (pts) => RANKS.slice().reverse().find(r => pts >= r.min) || RANKS[0];
export const getNext  = (pts) => RANKS.find(r => pts < r.max && pts >= r.min);
export const pct      = (a, b) => Math.min(100, Math.max(0, Math.round((a / b) * 100)));
export const sevColor = (s) => s === "High" ? C.coral : s === "Medium" ? C.gold : C.green;
export const medColor = (i) => i === 0 ? C.gold : i === 1 ? "#94a3b8" : "#cd7f32";

// ── Badges ────────────────────────────────────────────────────────────────────
export const BADGES = [
  { id:"first_report",  icon:"location_on",    label:"First Report",    desc:"Submit your first waste hotspot report",          color:C.primary },
  { id:"first_cleanup", icon:"cleaning_services",label:"First Cleanup", desc:"Complete your first cleanup mission",             color:C.green   },
  { id:"photo_pro",     icon:"add_a_photo",    label:"Photo Reporter",  desc:"Upload a photo with a report",                    color:C.gold    },
  { id:"streak_5",      icon:"local_fire_department",label:"5-Day Streak",desc:"Report 5 consecutive days",                    color:C.coral   },
  { id:"week_warrior",  icon:"bolt",           label:"Week Warrior",    desc:"Submit 7 reports in one week",                    color:C.blue  },
  { id:"team_player",   icon:"group",          label:"Team Player",     desc:"Join or create a cleanup team",                   color:C.blue    },
  { id:"limbe_local",   icon:"beach_access",   label:"Limbe Local",     desc:"Report on Limbe Beach 3 times",                   color:C.primary },
  { id:"kribi_king",    icon:"stars",          label:"Kribi King",      desc:"Report on Kribi Beach 5 times",                   color:C.gold    },
  { id:"legend_100",    icon:"diamond",        label:"100 Reports",     desc:"Submit 100 total reports",                        color:"#f472b6" },
  { id:"cleanup_hero",  icon:"emoji_events",   label:"Cleanup Hero",    desc:"Complete 20 cleanup missions",                    color:C.green   },
  { id:"monthly_champ", icon:"military_tech",  label:"Monthly Champ",   desc:"Top 3 on monthly leaderboard",                   color:C.gold    },
  { id:"month_streak",  icon:"whatshot",       label:"30-Day Streak",   desc:"Report every day for 30 days",                   color:"#fbbf24" },
];

// ── Challenges ────────────────────────────────────────────────────────────────
export const REWARDS = [
  { id:"r1",  cat:"digital",  matIcon:"signal_cellular_alt", name:"500MB Data Bundle",    brand:"MTN/Orange",  pts:200,  stock:99, hot:true  },
  { id:"r2",  cat:"digital",  matIcon:"phone_android",       name:"1000 XAF Airtime",     brand:"MTN/Orange",  pts:300,  stock:99, hot:false },
  { id:"r3",  cat:"digital",  matIcon:"wifi",                name:"1GB Data Bundle",      brand:"MTN/Orange",  pts:450,  stock:99, hot:true  },
  { id:"r4",  cat:"merch",    matIcon:"checkroom",           name:"CoastClear T-Shirt",   brand:"Limited Ed.", pts:800,  stock:12, hot:true  },
  { id:"r5",  cat:"merch",    matIcon:"style",               name:"CoastClear Cap",       brand:"Limited Ed.", pts:600,  stock:20, hot:false },
  { id:"r6",  cat:"merch",    matIcon:"water_drop",          name:"Eco Water Bottle",     brand:"CoastClear",  pts:700,  stock:15, hot:true  },
  { id:"r7",  cat:"school",   matIcon:"menu_book",           name:"Notebook Bundle",      brand:"CoastClear",  pts:400,  stock:30, hot:false },
  { id:"r8",  cat:"school",   matIcon:"backpack",            name:"Stationery Kit",       brand:"CoastClear",  pts:500,  stock:25, hot:false },
  { id:"r9",  cat:"cleanup",  matIcon:"glove",               name:"Beach Cleanup Kit",    brand:"CoastClear",  pts:350,  stock:40, hot:false },
  { id:"r10", cat:"cleanup",  matIcon:"recycling",           name:"Pro Cleanup Gear",     brand:"CoastClear",  pts:1200, stock:5,  hot:true  },
  { id:"r11", cat:"legend",   matIcon:"workspace_premium",   name:"Legend Pack (All)",    brand:"Exclusive",   pts:5000, stock:3,  hot:true  },
];

// ── Beaches ───────────────────────────────────────────────────────────────────
export const BEACHES = [
  { id:"limbe",  name:"Limbe Down Beach",  region:"South West", color:C.coral,   lat:4.0060, lng:9.2054 },
  { id:"kribi",  name:"Kribi Beach",       region:"South",      color:C.gold,    lat:2.9423, lng:9.9175 },
  { id:"batoke", name:"Batoke Beach",      region:"South West", color:C.coral,   lat:3.9847, lng:9.1673 },
  { id:"seme",   name:"Seme Beach",        region:"South West", color:C.green,   lat:4.0156, lng:9.3982 },
  { id:"idenau", name:"Idenau Beach",      region:"South West", color:C.gold,    lat:4.0567, lng:8.9869 },
  { id:"londji", name:"Londji Beach",      region:"South",      color:C.primary, lat:2.8712, lng:9.8956 },
  { id:"wouri",  name:"Wouri Estuary",     region:"Littoral",   color:C.coral,   lat:4.0247, lng:9.7031 },
];

// ── Waste Types ───────────────────────────────────────────────────────────────
export const WASTE_TYPES = [
  "Plastic Bottles", "Fishing Nets", "Polythene Bags",
  "Foam/Styrofoam",  "Mixed Waste",  "Glass",
  "Metal Cans",      "Tires",        "Medical Waste",
];

// ── Role config ───────────────────────────────────────────────────────────────
// ── Blog categories ───────────────────────────────────────────────────────────
export const BLOG_CATEGORIES = [
  { id:"all",       label:"All Articles",   matIcon:"article"         },
  { id:"pollution", label:"Pollution",      matIcon:"delete_sweep"    },
  { id:"wildlife",  label:"Marine Life",    matIcon:"set_meal"        },
  { id:"action",    label:"Take Action",    matIcon:"volunteer_activism"},
  { id:"science",   label:"Science",        matIcon:"science"          },
  { id:"community", label:"Community",      matIcon:"groups"           },
];

// ── Seed blog articles (static, shown if Firestore empty) ─────────────────────
export const SEED_BLOGS = [
  {
    id: "b1",
    title: "78% of Surveyed Fishermen in Limbe Report Damaged Nets from Plastic Waste",
    category: "pollution",
    coverImage: "https://images.unsplash.com/photo-1621451537084-482c73073a0f?w=800&q=80",
    readTime: "5 min read",
    author: "CoastClear Editorial",
    createdAt: { seconds: Date.now()/1000 - 86400*3 },
    excerpt: "A recent study of six beaches in Limbe and Kribi municipalities reveals alarming levels of marine litter — with 78.6% of Limbe fishermen reporting damage to nets and equipment from plastic pollution.",
    content: `Plastic waste has reached crisis levels along Cameroon's coastline. Research conducted across six beaches in the Limbe and Kribi municipalities found that the most common waste types include abandoned fishing nets, polythene bags, and foam debris.\n\nThe study found:\n• **Idenau beach**: Wood fragments (28%), Plastics (23.8%)\n• **Limbe Down Beach**: Plastics (24%), Paper (21%), Wood (20%)\n• **78.6%** of Limbe fishermen report nets damaged by plastic waste\n• **81.8%** of Kribi respondents identify plastic as the #1 problem\n\nThe economic impact is severe. Approximately 48% of fishermen report reduced income due to damaged equipment and smaller catches. Hotel operators confirm beach pollution has reduced tourist bookings.\n\n**What You Can Do**\nJoining CoastClear is your first step. Every report you submit feeds into a database that NGOs and government officials use to prioritize cleanup efforts. Your photo evidence creates undeniable proof of where action is needed most.\n\nASCOA, Cameroon's leading coastal NGO, has removed over **1,200 tons of waste** since 2022 using community data to guide their operations.`,
    tags: ["Limbe", "Fishing", "Research", "Plastic Waste"],
  },
  {
    id: "b2",
    title: "Marine Turtles, Manatees & Mangroves: What's at Stake on Cameroon's Coast",
    category: "wildlife",
    coverImage: "https://images.unsplash.com/photo-1591025207163-942350e47db2?w=800&q=80",
    readTime: "6 min read",
    author: "CoastClear Editorial",
    createdAt: { seconds: Date.now()/1000 - 86400*7 },
    excerpt: "The 7km Ngeme-Idenau coastal stretch hosts four species of marine turtles and the critically endangered African manatee. Plastic pollution now threatens these habitats directly.",
    content: `The Ngeme-Idenau coastal area is one of West Africa's most biodiverse marine zones. Its black sand beaches and dense mangrove forests serve as critical nesting grounds for wildlife that appears nowhere else in the region.\n\n**Species Under Threat**\n• Four species of marine turtles (including leatherback and olive ridley)\n• The African manatee — listed as Vulnerable by IUCN\n• The critically endangered sociable lapwing\n• Numerous fish species that sustain coastal fishing communities\n\n**How Plastic Reaches the Ocean**\nCameroon's coastal trash contributes to global ocean pollution — with 60% being plastic. Heavy rainfall sweeps waste from inland communities through rivers into the Atlantic. Industrial fishing fleets further compound the problem through lost gear and waste disposal.\n\n**The Mangrove Connection**\nMangroves are being logged by fishermen for building materials and fish-smoking ovens. When mangroves disappear, shoreline erosion accelerates, wave energy increases, and wildlife loses critical habitat. CoastClear's map system tracks mangrove health alongside waste levels.\n\n**Your Reports Matter**\nEvery waste report near mangrove zones gets flagged in our system for priority NGO response. If you're near Idenau, Batoke, or the Wouri Estuary, your observations are especially valuable.`,
    tags: ["Wildlife", "Turtles", "Manatee", "Idenau", "Mangroves"],
  },
  {
    id: "b3",
    title: "How to Organize a Beach Cleanup in Your Community — A Step-by-Step Guide",
    category: "action",
    coverImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
    readTime: "8 min read",
    author: "CoastClear Editorial",
    createdAt: { seconds: Date.now()/1000 - 86400*14 },
    excerpt: "ASCOA has mobilized thousands of volunteers since 2022, removing over 10,000 plastic items from Limbe beach in a single event. Here's how you can run your own cleanup.",
    content: `ASCOA's 2025 Cameroon Beach Cleanup Day brought together over 600 volunteers and removed 10,000+ plastic items from Limbe beach in a single day. You don't need a large organization to make an impact — here's how to start.\n\n**Step 1: Choose Your Beach & Date**\nCheck the CoastClear map for the highest-severity beaches near you. Weekends typically attract more volunteers. Aim for morning cleanups (7–11am) before the heat.\n\n**Step 2: Gather Equipment**\n• Gloves (rubber or leather) for each volunteer\n• Refuse sacks (at least 3 per person)\n• Tongs or grabbers for safe handling\n• Buckets for sorting recyclables\n• Sunscreen and water for participants\n\n**Step 3: Register on CoastClear**\nCreate a Cleanup Mission on CoastClear before you go. This alerts nearby volunteers, tags your location on the map, and ensures your efforts count toward community totals.\n\n**Step 4: Sort Your Waste**\nSeparate plastics, glass, metals, and organic waste. Contact your local Mairie (council) or a recycling partner for proper disposal. ASCOA maintains a network of drop-off points near major beaches.\n\n**Step 5: Document Everything**\nUse CoastClear's Before & After photo system. Before photos show the problem; after photos prove the impact. Both are submitted to our Firestore database and visible to NGO coordinators nationwide.\n\n**Step 6: Claim Your Points**\nEvery verified cleanup earns bonus points on top of your report submissions. Top monthly performers receive airtime, merchandise, and recognition in our leaderboard.`,
    tags: ["Guide", "Cleanup", "ASCOA", "Community", "Volunteers"],
  },
  {
    id: "b4",
    title: "Microplastics Found in Fish from Limbe Waters — What This Means for Food Safety",
    category: "science",
    coverImage: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80",
    readTime: "4 min read",
    author: "CoastClear Editorial",
    createdAt: { seconds: Date.now()/1000 - 86400*21 },
    excerpt: "Research confirms microplastic particles in fish and seafood from Limbe's fishing communities, creating food security and public health concerns that go beyond the beach.",
    content: `Plastic waste doesn't just pollute beaches — it enters the food chain. Research in Limbe has confirmed the presence of microplastic particles in fish and seafood caught from coastal waters, raising urgent food safety concerns.\n\n**What Are Microplastics?**\nWhen plastic breaks down in the ocean, it fragments into particles smaller than 5mm — microplastics. These are ingested by fish, shellfish, and other marine organisms. When humans eat these fish, the particles transfer up the food chain.\n\n**Sources of Limbe's Microplastic Problem**\n• Runoff from urban centers (Limbe, Douala) via rivers\n• Plastic from neighboring Nigeria and Equatorial Guinea carried by currents\n• Imported goods packaging that enters waterways\n• Industrial fishing fleet waste\n\n**Health Implications**\nMicroplastics can carry persistent organic pollutants (POPs) and heavy metals. Long-term exposure is linked to hormonal disruption and inflammation. For communities dependent on fish as a primary protein source, this is a critical concern.\n\n**What Science Recommends**\n1. Reduce single-use plastics at source\n2. Improve waste collection infrastructure\n3. Regular monitoring of fish catches for contamination\n4. Community education on safe fish handling\n\n**How CoastClear Helps**\nOur AI waste analysis system can identify plastic types in your reports. This data helps researchers track which plastics are most prevalent near fishing zones, enabling targeted intervention.`,
    tags: ["Microplastics", "Food Safety", "Science", "Fishing", "Health"],
  },
  {
    id: "b5",
    title: "The Plastic Boat of Londji: Local Innovation Turning Ocean Waste into Opportunity",
    category: "community",
    coverImage: "https://images.unsplash.com/photo-1503455637927-730bce8583c0?w=800&q=80",
    readTime: "5 min read",
    author: "CoastClear Editorial",
    createdAt: { seconds: Date.now()/1000 - 86400*30 },
    excerpt: "Researchers and local fishermen in Londji, Kribi, built a fully functional 5.5m boat using 850 recycled PET bottles. It now operates as an ecotourism vessel — proof that innovation can transform the plastic crisis.",
    content: `In Londji beach, Kribi, a remarkable experiment turned ocean waste into opportunity. Using 850 one-liter PET plastic bottles collected from the beach, researchers in partnership with local fishermen constructed a 5.5-metre ecological boat capable of carrying three adults.\n\n**The Build**\nThe bottles were cleaned, sorted by color, and assembled into a structural flotation system. Traditional boat-building knowledge from local fishermen was combined with engineering principles from academic researchers. The resulting vessel is stable, lightweight, and fully recyclable.\n\n**Impact**\nThe boat operates as an ecotourism vessel for Londji's growing adventure tourism industry. It has:\n• Removed 850 PET bottles from the coastal environment\n• Created employment for local guides\n• Attracted media attention internationally\n• Inspired similar projects in Limbe and Batoke\n\n**Traditional Knowledge as Innovation**\nThe project highlights how indigenous coastal knowledge — including navigation, tidal patterns, and materials science — can accelerate environmental solutions. This is why CoastClear's design includes Pidgin language support and locally relevant beach data.\n\n**What This Means for CoastClear Users**\nWhen you collect and categorize PET plastic in your waste reports, that data helps identify collection points for recycling initiatives like this. Your "Plastic Bottles" reports aren't just numbers — they're coordinates for future innovation.\n\nASCOA is exploring scaling the Londji model to other beaches. Contact them at cbc2025@ascoa-cm.org to get involved.`,
    tags: ["Innovation", "Recycling", "Londji", "Kribi", "Ecotourism", "Community"],
  },
];
